import type { OrgDto } from '../dtos/OrgDto.js';
import { getSupabaseClient } from '../../../../core/supabase/client.js';

export interface OrgRemoteListOptions {
  limit?: number;
  offset?: number;
}

export interface OrgRemoteCreatePayload {
  name: string;
  slug?: string;
  description?: string | null;
  createdBy: string;
}

export interface OrgRemoteUpdatePayload {
  name?: string | null;
  slug?: string | null;
  description?: string | null;
}

export class OrgRemoteDataSource {
  async getOrgById(id: string): Promise<OrgDto | null> {
    const supabase = getSupabaseClient() as any;
    const { data, error } = await supabase
      .from('orgs')
      .select('id, slug, name, description, created_by, created_at')
      .eq('id', id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) return null;

    return {
      id: data.id,
      slug: data.slug,
      name: data.name,
      description: data.description ?? null,
      createdBy: data.created_by,
      createdAt: data.created_at ?? null,
    };
  }

  async listOrgs(options?: OrgRemoteListOptions): Promise<OrgDto[]> {
    const supabase = getSupabaseClient() as any;
    let query = supabase
      .from('orgs')
      .select('id, slug, name, description, created_by, created_at')
      .order('created_at', { ascending: false });

    if (typeof options?.offset === 'number' && typeof options?.limit === 'number') {
      query = query.range(options.offset, options.offset + options.limit - 1);
    } else if (typeof options?.limit === 'number') {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;
    if (error) throw new Error(error.message);

    type OrgRow = { id: string; slug: string; name: string; description?: string | null; created_by: string; created_at?: string | null };
    return (data ?? []).map((org: OrgRow): OrgDto => ({
      id: org.id,
      slug: org.slug,
      name: org.name,
      description: org.description ?? null,
      createdBy: org.created_by,
      createdAt: org.created_at ?? null,
    }));
  }

  async createOrg(payload: OrgRemoteCreatePayload): Promise<OrgDto | null> {
    const supabase = getSupabaseClient() as any;
    const { data, error } = await supabase
      .from('orgs')
      .insert({
        name: payload.name,
        slug: payload.slug ?? undefined,
        description: payload.description ?? null,
        created_by: payload.createdBy,
      })
      .select('id, slug, name, description, created_by, created_at')
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) return null;

    return {
      id: data.id,
      slug: data.slug,
      name: data.name,
      description: data.description ?? null,
      createdBy: data.created_by,
      createdAt: data.created_at ?? null,
    };
  }

  async updateOrg(id: string, payload: OrgRemoteUpdatePayload): Promise<OrgDto | null> {
    const supabase = getSupabaseClient() as any;
    const { data, error } = await supabase
      .from('orgs')
      .update({
        name: payload.name,
        ...(payload.slug !== undefined && { slug: payload.slug }),
        description: payload.description ?? null,
      })
      .eq('id', id)
      .select('id, slug, name, description, created_by, created_at')
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) return null;

    return {
      id: data.id,
      slug: data.slug,
      name: data.name,
      description: data.description ?? null,
      createdBy: data.created_by,
      createdAt: data.created_at ?? null,
    };
  }

  async checkSlugExists(slug: string, excludeId?: string): Promise<boolean> {
    const supabase = getSupabaseClient() as any;
    let query = supabase.from('orgs').select('id').eq('slug', slug).limit(1);
    if (excludeId) query = query.neq('id', excludeId);
    const { data, error } = await query;
    if (error) throw new Error(error.message);
    return (data ?? []).length > 0;
  }

  async deleteOrg(id: string): Promise<void> {
    const supabase = getSupabaseClient() as any;
    const { error } = await supabase.from('orgs').delete().eq('id', id);
    if (error) throw new Error(error.message);
  }

  async transferOrgOwner(orgId: string, newOwnerId: string): Promise<void> {
    const supabase = getSupabaseClient() as any;
    const { error } = await supabase.rpc('transfer_org_owner', {
      p_org_id: orgId,
      p_new_owner_id: newOwnerId,
    });
    if (error) throw new Error(error.message);
  }
}
